import React, { useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  useStaffReportByIdQuery,
  useStaffReportExportByIdQuery
} from '@/services/staff.service';
import { formatCurrency } from '@/components/ReportMangers/common/utils/reportUtils';
import { StaffReportResponse } from '@/constants/Interfaces/CommonInterfaces';

const StaffIndividualReport: React.FC = () => {
  const { staffId } = useParams<{ staffId: string }>();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const start = queryParams.get('start');
  const end = queryParams.get('end');

  const payload = useMemo(() => {
    const params: Record<string, string> = {};
    if (start) params.start = start;
    if (end) params.end = end;
    // Ensure staff_id is a valid number
    if (staffId && !isNaN(Number(staffId))) {
      params.staff_id = String(staffId);
    }
    return new URLSearchParams(params).toString();
  }, [start, end, staffId]);

  const { data, isLoading: isLoadingReport } = useStaffReportByIdQuery(
    staffId as string,
    payload
  );

  // Process the API response - it returns items array with monthly data and staff object
  const reportData = useMemo(() => {
    // The query returns either the response with data property or direct data
    // based on how useStaffReportByIdQuery is implemented (it returns response?.data || response)
    const apiResponse = data as unknown as StaffReportResponse;
    const items = apiResponse?.items || [];
    const staffInfo = apiResponse?.staff || {};

    // Find the total row (where month is "Total")
    const totalRow = items.find(
      item => item.month && item.month.toLowerCase() === 'total'
    );

    // Filter out total row to get monthly data
    const monthlyData = items.filter(
      item => item.month && item.month.toLowerCase() !== 'total'
    );

    // Get personal details from staff object
    const personalDetails = {
      name: staffInfo.name || '',
      phone: staffInfo.phone || '',
      email: staffInfo.email || '',
      address_line_1: staffInfo.address_line_1 || '',
      address_line_2: staffInfo.address_line_2 || '',
      city: staffInfo.city || '',
      zip_code: staffInfo.zip_code || '',
      my_number: staffInfo.my_number || ''
    };

    return {
      personal_details: personalDetails,
      monthly_breakdown: monthlyData,
      totals: totalRow
        ? {
            total_gross: Number(totalRow.gross) || 0,
            total_tax: Number(totalRow.tax) || 0,
            total_after_tax: Number(totalRow.after_tax) || 0,
            total_health_insurance: Number(totalRow.health_insurance) || 0,
            total_pension: Number(totalRow.pension) || 0,
            total_insurance: Number(totalRow.insurance) || 0,
            total_net: Number(totalRow.net) || 0
          }
        : {
            total_gross: 0,
            total_tax: 0,
            total_after_tax: 0,
            total_health_insurance: 0,
            total_pension: 0,
            total_insurance: 0,
            total_net: 0
          }
    };
  }, [data]);

  const [exportType, setExportType] = useState<string>('');
  const exportPayload = useMemo(() => {
    if (!start || !end || !exportType || !staffId) return '';
    const params: Record<string, string> = {
      start,
      end,
      type: exportType,
      staff_id: staffId,
      pagination: 'false'
    };
    return new URLSearchParams(params).toString();
  }, [start, end, exportType, staffId]);

  const [exportEnabled, setExportEnabled] = useState<boolean>(false);
  const { isLoading: exportLoading, error: exportError } =
    useStaffReportExportByIdQuery(
      staffId as string,
      exportPayload,
      exportEnabled
    );

  const canExport = Boolean(start) && Boolean(end) && Boolean(staffId);

  const handleExport = (type: 'pdf' | 'excel' | 'print') => {
    if (!canExport) {
      alert('Please provide a valid date range to export.');
      return;
    }
    setExportEnabled(false);
    setExportType(type);
    setTimeout(() => {
      setExportEnabled(true);
    }, 0);
  };

  // Format period display
  const formatPeriod = () => {
    if (!start || !end) return '';
    const startDate = new Date(start);
    const endDate = new Date(end);
    const startMonth = startDate.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
    const endMonth = endDate.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
    return `${startMonth} to ${endMonth}`;
  };

  const fullAddress = [
    reportData.personal_details.address_line_1,
    reportData.personal_details.address_line_2,
    reportData.personal_details.city,
    reportData.personal_details.zip_code
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className='relative'>
      <div className='p-6 bg-gradient-to-br from-slate-50 to-white min-h-screen'>
        <div className='bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden'>
          {/* Header Section */}
          <div className='bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-6'>
            <div className='flex justify-between items-start'>
              <div>
                <h1 className='text-2xl font-bold mb-2 text-white'>
                  {reportData.personal_details.name} – Income Salary Report
                </h1>
                <p className='text-blue-100 text-sm'>{formatPeriod()}</p>
              </div>
              <div className='flex gap-3'>
                <button
                  className='bg-white text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors font-medium shadow-sm'
                  onClick={() => handleExport('pdf')}
                  disabled={exportLoading}
                >
                  PDF
                </button>
                <button
                  className='bg-white text-green-600 px-4 py-2 rounded-lg hover:bg-green-50 transition-colors font-medium shadow-sm'
                  onClick={() => handleExport('excel')}
                  disabled={exportLoading}
                >
                  Excel
                </button>
                <button
                  className='bg-white text-slate-600 px-4 py-2 rounded-lg hover:bg-slate-50 transition-colors font-medium shadow-sm'
                  onClick={() => handleExport('print')}
                  disabled={exportLoading}
                >
                  Print
                </button>
              </div>
            </div>
            {exportLoading && (
              <p className='text-blue-100 text-sm mt-2'>Exporting...</p>
            )}
            {exportError && (
              <p className='text-red-200 text-sm mt-2'>Export failed</p>
            )}
          </div>

          {isLoadingReport ? (
            <div className='p-8'>
              <div className='animate-pulse space-y-6'>
                <div className='h-8 bg-slate-200 rounded w-1/3'></div>
                <div className='space-y-3'>
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className='h-6 bg-slate-200 rounded'></div>
                  ))}
                </div>
                <div className='h-64 bg-slate-200 rounded'></div>
              </div>
            </div>
          ) : (
            <div className='p-8'>
              {/* Personal Details Section */}
              <div className='mb-8'>
                <h2 className='text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-600'>
                  Personal Details
                </h2>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-6 rounded-lg'>
                  <div className='flex'>
                    <span className='font-semibold text-slate-700 w-32'>
                      Phone:
                    </span>
                    <span className='text-gray-900'>
                      {reportData.personal_details.phone}
                    </span>
                  </div>
                  <div className='flex'>
                    <span className='font-semibold text-slate-700 w-32'>
                      Email:
                    </span>
                    <span className='text-gray-900'>
                      {reportData.personal_details.email}
                    </span>
                  </div>
                  <div className='flex md:col-span-2'>
                    <span className='font-semibold text-slate-700 w-32'>
                      Address:
                    </span>
                    <span className='text-gray-900'>{fullAddress}</span>
                  </div>
                  <div className='flex'>
                    <span className='font-semibold text-slate-700 w-32'>
                      My Number:
                    </span>
                    <span className='text-gray-900'>
                      {reportData.personal_details.my_number}
                    </span>
                  </div>
                </div>
              </div>

              {/* Monthly Breakdown Table */}
              <div>
                <h2 className='text-xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-blue-600'>
                  Monthly Breakdown
                </h2>
                <div className='overflow-x-auto'>
                  <table className='w-full text-sm border-collapse'>
                    <thead className='bg-slate-100'>
                      <tr>
                        <th className='py-3 px-4 font-semibold text-left border'>
                          Month
                        </th>
                        <th className='py-3 px-4 font-semibold text-right border'>
                          Gross
                        </th>
                        <th className='py-3 px-4 font-semibold text-right border'>
                          Tax
                        </th>
                        <th className='py-3 px-4 font-semibold text-right border'>
                          After Tax
                        </th>
                        <th className='py-3 px-4 font-semibold text-right border'>
                          Health Insurance
                        </th>
                        <th className='py-3 px-4 font-semibold text-right border'>
                          Pension
                        </th>
                        <th className='py-3 px-4 font-semibold text-right border'>
                          Insurance
                        </th>
                        <th className='py-3 px-4 font-semibold text-right border'>
                          Net
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.monthly_breakdown.length === 0 ? (
                        <tr>
                          <td
                            colSpan={7}
                            className='text-center text-gray-500 py-8 border'
                          >
                            No data available for the selected period
                          </td>
                        </tr>
                      ) : (
                        reportData.monthly_breakdown.map(
                          (row: any, idx: number) => (
                            <tr
                              key={idx}
                              className='odd:bg-white even:bg-slate-50 hover:bg-blue-50'
                            >
                              <td className='py-3 px-4 border font-medium'>
                                {row.month}
                              </td>
                              <td className='py-3 px-4 text-right border'>
                                {formatCurrency(row.gross)}
                              </td>
                              <td className='py-3 px-4 text-right border text-red-600'>
                                {formatCurrency(row.tax)}
                              </td>
                              <td className='py-3 px-4 text-right border'>
                                {formatCurrency(row.after_tax)}
                              </td>
                              <td className='py-3 px-4 text-right border text-orange-600'>
                                {formatCurrency(row.health_insurance)}
                              </td>
                              <td className='py-3 px-4 text-right border text-orange-600'>
                                {formatCurrency(row.pension)}
                              </td>
                              <td className='py-3 px-4 text-right border text-orange-600'>
                                {formatCurrency(row.insurance)}
                              </td>
                              <td className='py-3 px-4 text-right border text-green-600 font-semibold'>
                                {formatCurrency(row.net)}
                              </td>
                            </tr>
                          )
                        )
                      )}
                      {/* Totals Row */}
                      <tr className='bg-slate-200 font-bold'>
                        <td className='py-3 px-4 border'>Totals</td>
                        <td className='py-3 px-4 text-right border'>
                          {formatCurrency(reportData.totals.total_gross)}
                        </td>
                        <td className='py-3 px-4 text-right border text-red-600'>
                          {formatCurrency(reportData.totals.total_tax)}
                        </td>
                        <td className='py-3 px-4 text-right border'>
                          {formatCurrency(reportData.totals.total_after_tax)}
                        </td>
                        <td className='py-3 px-4 text-right border text-orange-600'>
                          {formatCurrency(
                            reportData.totals.total_health_insurance
                          )}
                        </td>
                        <td className='py-3 px-4 text-right border text-orange-600'>
                          {formatCurrency(reportData.totals.total_pension)}
                        </td>
                        <td className='py-3 px-4 text-right border text-orange-600'>
                          {formatCurrency(reportData.totals.total_insurance)}
                        </td>
                        <td className='py-3 px-4 text-right border text-green-600'>
                          {formatCurrency(reportData.totals.total_net)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffIndividualReport;
